import { useState } from "react";
import { useRejectWalletEntry } from "../hooks/useWallets";
import type { WalletLedgerEntry } from "../services/walletsApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";

interface Props {
  entry: WalletLedgerEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RejectWalletDialog = ({ entry, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const mutation = useRejectWalletEntry();
  const [reason, setReason] = useState("");

  const submit = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      notification.error("Rejection reason must be at least 10 characters.");
      return;
    }
    mutation.mutate(
      { entryId: entry.id, payload: { rejectionReason: trimmed } },
      {
        onSuccess: () => {
          notification.success("Entry rejected.");
          setReason("");
          onOpenChange(false);
        },
        onError: () => {
          notification.error("Failed to reject entry.");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject wallet entry</DialogTitle>
          <DialogDescription>
            {entry.type} — KES {entry.amount.toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <Field>
          <FieldLabel>Reason <span className="text-destructive">*</span></FieldLabel>
          <Textarea
            placeholder="At least 10 characters."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={mutation.isPending}
            rows={4}
          />
        </Field>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
